import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import Select from 'react-select';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import { getAllExpedients } from '../../redux/actions/expedients';
import {
  getAllDevices,
  groupConfigs,
  groupSync
} from '../../redux/actions/devices';
import { getAllPABXs } from '../../redux/actions/pabxs';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
/* form-validation */
import schema from '../../form-validation/groupsConfig';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption
} from '../../utils/select';
import sortable from '../../utils/sortable';
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';

function Add () {
  const INITIAL_VALUES = {
    device: '',
    expedient: '',
    logged: false,
    groupMembers: [],
    supervisorDevice: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset, setValue, setError } = useForm(
    {
      validationSchema: schema,
      reValidateMode: 'onSubmit'
    }
  );
  const pabxs = useSelector(state => state.pabxs.data);
  const devices = useSelector(state => state.devices.data);
  const devicesStore = useSelector(state => state.devices);
  const expedients = useSelector(state => state.expedients.data);
  const [data, setData] = useState(INITIAL_VALUES);
  const [selectedOptions, setSelectedOptions] = useState({ groupMembers: [] });
  const [groups, setGroups] = useState([]);
  const [deviceOptions, setDeviceOptions] = useState([firstOption]);
  const [expedientOptions, setExpedientOptions] = useState([firstOption]);
  const [groupMembersOptions, setGroupMembersOptions] = useState([]);
  const [disabledForm, setDisabledForm] = useState(true);

  useEffect(() => {
    dispatch(getAllExpedients());
    dispatch(getAllDevices());
    dispatch(getAllPABXs());
  }, [dispatch]);

  useEffect(() => {
    if (pabxs && pabxs[0]) {
      const supervisorDevice = pabxs[0].supervisorDevice || '';
      setData({ ...data, supervisorDevice });
    }
  }, [pabxs]);

  useEffect(() => {
    if (devices.length) {
      const groups = devices.filter(device => device.type === 'G');
      setGroups([...groups]);

      const options = groups
        .map(device => ({
          value: device._id,
          sortValue: `${device.number} | ${device.name} - Dpto: ${device.department.name}`,
          label: `${device.number} | ${device.name} - Dpto: ${device.department.name}`,
          number: device.number
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setDeviceOptions([...options]);

      const selectedGroup = groups.find(group => group._id === data.device);
      if (selectedGroup) {
        const group = {
          device: selectedGroup._id,
          logged: selectedGroup.logged,
          expedient: selectedGroup.expedient,
          groupMembers: selectedGroup.groupMembers.map(grp => grp.extension)
        };
        setData({ ...data, ...group });
        const groupMembers = groupMembersOptions.filter(opt =>
          group.groupMembers.includes(opt.value)
        );
        setSelectedOptions({ ...selectedOptions, groupMembers });
        setDisabledForm(false);
      }
    }
  }, [devices, setDeviceOptions, firstOption, setGroups]);

  useEffect(() => {
    if (expedients.length) {
      const options = expedients
        .map(expedient => ({
          value: expedient._id,
          sortValue: `${expedient.name} - Dpto: ${expedient.department.name}`,
          label: `${expedient.name} - Dpto: ${expedient.department.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setExpedientOptions([...options]);
    }
  }, [expedients, setExpedientOptions, firstOption]);

  useEffect(() => {
    if (devices.length) {
      const extensions = devices
        .filter(device => device.type === 'E')
        .map(device => ({
          value: device._id,
          sortValue: `${device.number} - Dpto: ${device.department.name}`,
          label: `${device.number} - Dpto: ${device.department.name}`,
          number: device.number
        }))
        .sort(sortable);

      setGroupMembersOptions([...extensions]);
    }
  }, [devices, setGroupMembersOptions]);

  const submit = () => {
    dispatch(groupConfigs(data, submitButton.current));
  };

  const handleChangeSelect = async evt => {
    const { name, number, value } = evt;
    if (name === 'device') {
      if (value) {
        dispatch(groupSync({ group: number }, submitButton.current));
        const selectedGroup = groups.find(group => group._id === value);
        const group = {
          device: selectedGroup._id,
          logged: selectedGroup.logged,
          expedient: selectedGroup.expedient,
          groupMembers: selectedGroup.groupMembers.map(grp => grp.extension)
        };
        setData({ ...data, ...group });
        const groupMembers = groupMembersOptions.filter(opt =>
          group.groupMembers.includes(opt.value)
        );
        setSelectedOptions({ ...selectedOptions, groupMembers });
      } else {
        setDisabledForm(true);
        setData({ ...INITIAL_VALUES });
        Object.keys(selectedOptions).forEach(key =>
          setSelectedOptions({ ...selectedOptions, [key]: [] })
        );
      }
      // Reseta o form-validation do form
      reset();
    } else {
      setData({
        ...data,
        [name]: value
      });
    }
    value ? setError(name) : setError(name, 'required', 'field is required');
    setValue({ [name]: value });
  };

  return (
    <div className="container-fluid">
      {devicesStore.groupConfigSuccess && (
        <AlertMsg type="success" msg="Grupo configurado com sucesso" />
      )}
      {devicesStore.groupConfigError && (
        <AlertMsg
          type="danger"
          msg={`${
            devicesStore.errorContent.response &&
            devicesStore.errorContent.response.status !== 400
              ? 'Falha durante configuração do grupo'
              : 'Verifique se o administrador do PABX está deslogado'
          }
            `}
        />
      )}
      {!devicesStore.groupConfigSuccess && !devicesStore.groupConfigError && (
        <AlertMsg type="info" msg="Área configuração de grupos" />
      )}
      <FormHeader title="Configurar Grupo" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-group">
          <label htmlFor="device">
            Grupo Lógico<span className="field-required">*</span>
          </label>
          <Select
            name="device"
            ref={register}
            defaultValue={deviceOptions[0]}
            onChange={evt => handleChangeSelect({ ...evt, name: 'device' })}
            options={deviceOptions}
            isSearchable={true}
            theme={customThemes}
            styles={customStyle}
            filterOption={customFilter}
            noOptionsMessage={() => 'Nenhum registro encontrado'}
          />
          {!disabledForm &&
            errors.device &&
            errors.device.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="row">
          <div className="col-sm-5 col-md-3">
            <div className="form-group">
              <label htmlFor="logged">
                Ramais Sempre Logados<span className="field-required">*</span>
              </label>
              <div className="checkbox">
                <Switch
                  type="checkbox"
                  color="success"
                  name="logged"
                  onChange={evt => {
                    setData({
                      ...data,
                      [evt.target.name]: evt.target.checked
                    });
                  }}
                  disabled={disabledForm || devicesStore.isFetching}
                  checked={data.logged}
                >
                  {data.logged ? 'Ativado' : 'Desativado'}
                </Switch>
              </div>
            </div>
          </div>
          <div className="col-sm-7 col-md-9">
            <div className="form-group">
              <label htmlFor="expedient">
                Expediente<span className="field-required">*</span>
              </label>
              <Select
                name="expedient"
                ref={register}
                onChange={evt =>
                  handleChangeSelect({ ...evt, name: 'expedient' })
                }
                defaultValue={expedientOptions[0]}
                value={expedientOptions.find(
                  expedient => expedient.value === data.expedient
                )}
                options={expedientOptions}
                isSearchable={true}
                theme={customThemes}
                styles={customStyle}
                isDisabled={disabledForm || devicesStore.isFetching}
                filterOption={customFilter}
                noOptionsMessage={() => 'Nenhum registro encontrado'}
              />
              {!disabledForm &&
                errors.expedient &&
                errors.expedient.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <MultiSelect
          avaliableOptions={groupMembersOptions}
          selectedOptions={selectedOptions.groupMembers}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'groupMembers',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'groupMembers',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="label"
          title="Ramais"
          name="groupMembers"
          ref={register}
          error={!!errors.groupMembers}
          disabled={disabledForm || devicesStore.isFetching}
        />
        <FormFooter reference={submitButton} hideBackButton={true} />
      </form>
    </div>
  );
}

export default Add;
